import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Container,
  Card,
  CardContent,
  Grid,
  Avatar,
  useTheme,
  alpha,
  Fade,
  Slide,
  Skeleton,
  LinearProgress,
  Chip,
  Stack,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  LocationCity,
  Add,
  Refresh,
  Download,
  Search,
  FilterList,
  Clear,
  TrendingUp,
  Analytics
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSnackbar } from 'notistack';
import { cityService } from '../../services/cityService';

const City = () => {
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [newCityName, setNewCityName] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadCities();
  }, []);

  const loadCities = async () => {
    try {
      setLoading(true);
      const data = await cityService.getCities();
      // Normalize to array in case backend wraps payload like { data: [...] }
      const items = Array.isArray(data) ? data : (data?.data || []);
      setCities(items);
      applyFilters(items, searchQuery, statusFilter);
      enqueueSnackbar('Города загружены', { variant: 'success' });
    } catch (error) {
      console.error('Error loading cities:', error);
      enqueueSnackbar('Ошибка загрузки городов', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Search and filtering
  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    applyFilters(cities, query, statusFilter);
  };

  const handleStatusFilter = (event) => {
    const status = event.target.value;
    setStatusFilter(status);
    applyFilters(cities, searchQuery, status);
  };

  const applyFilters = (citiesData, query, status) => {
    let filtered = citiesData.filter(city => 
      city.name?.toLowerCase().includes(query)
    );

    if (status === 'active') {
      filtered = filtered.filter(city => city.status === 'active');
    } else if (status === 'inactive') {
      filtered = filtered.filter(city => city.status === 'inactive');
    }

    setFilteredCities(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    applyFilters(cities, '', 'all');
  };

  // Export functionality
  const exportToExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();
      
      const exportData = filteredCities.map((city, index) => ({
        '№': index + 1,
        'Название': city.name,
        'Статус': city.status === 'active' ? 'Активный' : 'Неактивный',
        'Дата создания': new Date(city.createdAt || Date.now()).toLocaleString('ru-RU'),
        'Порядок': city.order || index
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      
      const colWidths = [
        { wch: 5 }, { wch: 20 }, { wch: 15 }, { wch: 20 }, { wch: 10 }
      ];
      worksheet['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Города');
      
      const fileName = `cities_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
      
      enqueueSnackbar('Данные экспортированы в Excel', { variant: 'success' });
    } catch (error) {
      console.error('Export error:', error);
      enqueueSnackbar('Ошибка экспорта данных', { variant: 'error' });
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = Array.from(cities);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setCities(items);
    try {
      // Преобразуем массив для отправки на сервер
      const orderedCities = items.map((city, index) => ({
        id: city._id,
        order: index
      }));
      await cityService.updateOrder(orderedCities);
    } catch (error) {
      console.error('Error updating order:', error);
      // Revert changes if update failed
      loadCities();
    }
  };

  const handleAddCity = () => {
    setEditingCity(null);
    setNewCityName('');
    setOpenDialog(true);
  };

  const handleEditCity = (city) => {
    setEditingCity(city);
    setNewCityName(city.name);
    setOpenDialog(true);
  };

  const handleDeleteCity = async (cityId) => {
    try {
      await cityService.deleteCity(cityId);
      await loadCities();
      enqueueSnackbar('Город удален', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting city:', error);
      enqueueSnackbar('Не удалось удалить город', { variant: 'error' });
    }
  };

  const handleToggleStatus = async (cityId) => {
    const city = cities.find(c => c._id === cityId);
    if (!city) {
      console.error('City not found');
      return;
    }
    const newStatus = city.status === 'active' ? 'inactive' : 'active';

    try {
      const updatedCity = await cityService.updateCity(cityId, { status: newStatus });
      setCities(cities.map(city =>
        city._id === cityId
          ? updatedCity
          : city
      ));
    } catch (error) {
      console.error('Error updating city status:', error);
    }
  };

  const handleSaveCity = async () => {
    try {
      const name = (newCityName || '').trim();
      if (!name) {
        enqueueSnackbar('Укажите название города', { variant: 'warning' });
        return;
      }
      if (editingCity) {
        await cityService.updateCity(editingCity._id, { name });
      } else {
        await cityService.createCity({ name });
      }
      // Reload from server to ensure immediate visibility and correct filters
      await loadCities();
      setOpenDialog(false);
      setNewCityName('');
      setEditingCity(null);
    } catch (error) {
      console.error('Error saving city:', error);
      const message = error?.response?.data?.message || error?.response?.data?.error || 'Не удалось сохранить город';
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Fade in={true} timeout={800}>
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {/* Hero Section */}
        <Paper 
          elevation={4}
          sx={{ 
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: 3,
            mb: 3,
            color: 'text.primary'
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main', mr: 2, width: 56, height: 56 }}>
                <LocationCity sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold">
                  Управление городами
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Настройка городов и их порядка отображения
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Экспорт в Excel">
                <IconButton 
                  onClick={exportToExcel}
                  disabled={filteredCities.length === 0 || loading}
                  sx={{ 
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  <Download />
                </IconButton>
              </Tooltip>
              <Tooltip title="Обновить данные">
                <IconButton 
                  onClick={loadCities}
                  disabled={loading}
                  sx={{ 
                    color: 'text.primary',
                    bgcolor: 'rgba(255,255,255,0.1)',
                    '&:hover': { bgcolor: 'action.selected', color: 'primary.main' },
                    '&:disabled': { opacity: 0.5 }
                  }}
                >
                  {loading ? <LinearProgress size={20} sx={{ color: 'text.primary' }} /> : <Refresh />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Paper>

        {/* Search and Filters */}
        <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Search color="primary" />
                Поиск и фильтры
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  size="small" 
                  onClick={clearFilters}
                  startIcon={<Clear />}
                  disabled={loading}
                >
                  Сбросить
                </Button>
                <Button 
                  variant="contained"
                  onClick={handleAddCity}
                  startIcon={<Add />}
                  disabled={loading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Добавить город
                </Button>
              </Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Поиск городов"
                  value={searchQuery}
                  onChange={handleSearch}
                  placeholder="Введите название города"
                  disabled={loading}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      }
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Статус</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={handleStatusFilter}
                    label="Статус"
                    disabled={loading}
                    startAdornment={<FilterList sx={{ mr: 1, color: 'text.secondary' }} />}
                    sx={{
                      borderRadius: 2,
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.primary.main,
                      }
                    }}
                  >
                    <MenuItem value="all">Все статусы</MenuItem>
                    <MenuItem value="active">Только активные</MenuItem>
                    <MenuItem value="inactive">Только неактивные</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card elevation={3} sx={{ p: 3, borderRadius: 3 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                    <Skeleton variant="text" width={100} height={24} />
                  </Box>
                  <Skeleton variant="text" width={80} height={48} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width={150} height={20} />
                </Card>
              </Grid>
            ))
          ) : (
            <>
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  elevation={3}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.15)}`
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                      <LocationCity />
                    </Avatar>
                    <Typography variant="h6" color="primary" fontWeight="bold">
                      Всего городов
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="primary">
                    {cities.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    из них отфильтровано: {filteredCities.length}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  elevation={3}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.success.main, 0.15)}`
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: theme.palette.success.main, mr: 2 }}>
                      <CheckCircleIcon />
                    </Avatar>
                    <Typography variant="h6" color="success.main" fontWeight="bold">
                      Активные
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="success.main">
                    {cities.filter(c => c.status === 'active').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    городов активны
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card 
                  elevation={3}
                  sx={{ 
                    p: 3, 
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 8px 25px ${alpha(theme.palette.error.main, 0.15)}`
                    }
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    <Avatar sx={{ bgcolor: theme.palette.error.main, mr: 2 }}>
                      <CancelIcon />
                    </Avatar>
                    <Typography variant="h6" color="error.main" fontWeight="bold">
                      Неактивные
                    </Typography>
                  </Box>
                  <Typography variant="h3" fontWeight="bold" color="error.main">
                    {cities.filter(c => c.status === 'inactive').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    городов неактивны
                  </Typography>
                </Card>
              </Grid>
            </>
          )}
        </Grid>

        {/* Cities Table */}
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="bold">
              🏙️ Список городов ({loading ? '...' : filteredCities.length})
            </Typography>
            {!loading && filteredCities.length === 0 && cities.length > 0 && (
              <Chip 
                icon={<FilterList />} 
                label="Нет результатов" 
                color="warning" 
                variant="outlined" 
              />
            )}
          </Box>
          {loading && <LinearProgress />}
          
          {loading ? (
            <Box sx={{ p: 3 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2 }}>
                  <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="text" width="30%" height={24} sx={{ mr: 2 }} />
                  <Skeleton variant="circular" width={32} height={32} sx={{ mr: 2 }} />
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Skeleton variant="circular" width={32} height={32} />
                  </Box>
                </Box>
              ))}
            </Box>
          ) : filteredCities.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <LocationCity sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {cities.length === 0 ? 'Нет городов' : 'Нет результатов'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {cities.length === 0 
                  ? 'Добавьте первый город для начала работы'
                  : 'Попробуйте изменить параметры поиска'
                }
              </Typography>
              {cities.length === 0 && (
                <Button 
                  variant="contained" 
                  startIcon={<Add />}
                  onClick={handleAddCity}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  Добавить первый город
                </Button>
              )}
            </Box>
          ) : (
            <TableContainer>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell width={50} sx={{ fontWeight: 'bold' }}>
                        <DragIndicatorIcon color="action" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Город</TableCell>
                      <TableCell width={120} sx={{ fontWeight: 'bold' }}>Статус</TableCell>
                      <TableCell width={150} align="right" sx={{ fontWeight: 'bold' }}>Действия</TableCell>
                    </TableRow>
                  </TableHead>
                  <Droppable droppableId="cities">
                    {(provided) => (
                      <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                        {filteredCities.map((city, index) => (
                          <Draggable key={city._id} draggableId={city._id} index={index}>
                            {(provided, snapshot) => (
                              <TableRow
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                sx={{
                                  backgroundColor: snapshot.isDragging ? alpha(theme.palette.primary.main, 0.1) : 'inherit',
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                    transform: 'translateX(4px)'
                                  }
                                }}
                              >
                                <TableCell {...provided.dragHandleProps}>
                                  <DragIndicatorIcon 
                                    sx={{ 
                                      color: snapshot.isDragging ? theme.palette.primary.main : 'action.active',
                                      cursor: 'grab',
                                      '&:active': { cursor: 'grabbing' }
                                    }} 
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body1" fontWeight="medium">
                                    {city.name}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    icon={city.status === 'active' ? <CheckCircleIcon /> : <CancelIcon />}
                                    label={city.status === 'active' ? 'Активный' : 'Неактивный'}
                                    color={city.status === 'active' ? 'success' : 'error'}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleToggleStatus(city._id, city.status)}
                                    sx={{ 
                                      cursor: 'pointer',
                                      '&:hover': { 
                                        backgroundColor: city.status === 'active' 
                                          ? alpha(theme.palette.success.main, 0.1)
                                          : alpha(theme.palette.error.main, 0.1)
                                      }
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Tooltip title="Редактировать">
                                      <IconButton
                                        onClick={() => handleEditCity(city)}
                                        size="small"
                                        sx={{
                                          color: theme.palette.primary.main,
                                          '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Удалить">
                                      <IconButton
                                        onClick={() => handleDeleteCity(city._id)}
                                        size="small"
                                        sx={{
                                          color: theme.palette.error.main,
                                          '&:hover': {
                                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                                            transform: 'scale(1.1)'
                                          }
                                        }}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </TableBody>
                    )}
                  </Droppable>
                </Table>
              </DragDropContext>
            </TableContainer>
          )}
        </Paper>

        {/* Enhanced Dialog */}
        <Dialog 
          open={openDialog} 
          onClose={() => setOpenDialog(false)}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
              backdropFilter: 'blur(10px)'
            }
          }}
        >
          <DialogTitle sx={{ 
            pb: 2, 
            bgcolor: 'background.paper',
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            <Avatar sx={{ bgcolor: 'action.selected', color: 'primary.main' }}>
              <LocationCity />
            </Avatar>
            {editingCity ? 'Редактировать город' : 'Добавить новый город'}
          </DialogTitle>
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              autoFocus
              margin="dense"
              label="Название города"
              fullWidth
              variant="outlined"
              value={newCityName}
              onChange={(e) => setNewCityName(e.target.value)}
              placeholder="Введите название города"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main,
                  }
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={() => setOpenDialog(false)}
              sx={{ borderRadius: 2 }}
            >
              Отмена
            </Button>
            <Button 
              onClick={handleSaveCity} 
              variant="contained"
              disabled={!newCityName.trim()}
              sx={{
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': {
                  background: theme.palette.action.disabledBackground
                }
              }}
            >
              {editingCity ? 'Сохранить изменения' : 'Добавить город'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Fade>
  );
};

export default City;
